import { toast, ToastOptions } from "react-toastify";

/**
 * Default configuration options for toast notifications.
 *
 * @remarks
 * These options define the default behavior and appearance of toast notifications,
 * such as their position on the screen, auto-close duration, progress bar visibility,
 * and interactivity features like draggable and pause on hover.
 *
 * @type {ToastOptions}
 * @property {"top-right"} position - The position of the toast notification on the screen.
 * @property {number} autoClose - Duration in milliseconds before the toast automatically closes.
 * @property {boolean} hideProgressBar - Whether to hide the progress bar.
 * @property {boolean} closeOnClick - Whether the toast closes when clicked.
 * @property {boolean} pauseOnHover - Whether the toast pauses auto-close on hover.
 * @property {boolean} draggable - Whether the toast can be dragged.
 * @property {number | undefined} progress - Manually set the progress bar value, or undefined for automatic.
 * @property {"colored"} theme - The theme style of the toast notification.
 */
const defaultOptions: ToastOptions = {
    position: "top-right",
    autoClose: 6000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
};

/**
 * Displays a success toast notification with the provided message and options.
 *
 * @param message - The message to display in the success toast.
 * @param options - Optional toast configuration options to override the defaults.
 */
export const showSuccess = (message: string, options: ToastOptions = {}) => {
    toast.success(message, { ...defaultOptions, ...options });
};

/**
 * Displays an error toast notification with the specified message and options.
 *
 * @param message - The error message to display in the toast notification.
 * @param options - Optional configuration for the toast notification. Merges with default options.
 */
export const showError = (message: string, options: ToastOptions = {}) => {
    toast.error(message, { ...defaultOptions, ...options });
};

/**
 * Displays an informational toast notification with the specified message and options.
 *
 * @param message - The message to display in the toast notification.
 * @param options - Optional configuration for the toast notification. Merges with default options.
 */
export const showInfo = (message: string, options: ToastOptions = {}) => {
    toast.info(message, { ...defaultOptions, ...options });
};

/**
 * Displays a warning toast notification with the specified message and options.
 *
 * @param message - The warning message to display in the toast notification.
 * @param options - Optional configuration for the toast notification. Merges with default options.
 */
export const showWarning = (message: string, options: ToastOptions = {}) => {
    toast.warning(message, { ...defaultOptions, ...options });
};
